// components/EmailEditor/SaveOptionsDialog.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Send, Clock, Save } from "lucide-react";
import dayjs from "dayjs";

const ScheduleOptions = ({ open, onClose, onSave, loading, setLoading }) => {
  const [selectedOption, setSelectedOption] = useState("now");
  const [scheduledDateTime, setScheduledDateTime] = useState(
    dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
  );
  const [error, setError] = useState("");

  const handleSave = () => {
    setError("");

    if (selectedOption === "schedule") {
      if (!scheduledDateTime || dayjs(scheduledDateTime).isBefore(dayjs())) {
        setError("Scheduled time must be in the future");
        return;
      }
    }

    const scheduledTime =
      selectedOption === "schedule"
        ? dayjs(scheduledDateTime).format("YYYY-MM-DDTHH:mm:ss")
        : null;

    setLoading(true);
    onSave(selectedOption, scheduledTime);
  };

  const handleClose = () => {
    setError("");
    setScheduledDateTime(dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
    setSelectedOption("now");
    setLoading(false);
    onClose();
  };

  const handleDateTimeChange = (e) => {
    setScheduledDateTime(e.target.value);
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #3498db 0%, #2980b9 100%)",
          color: "white",
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Schedule Email Template
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9, fontSize: "0.875rem" }}>
          Choose when to send your emails
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, pb: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
          >
            {/* Send Now Option */}
            <Box
              sx={{
                border: "2px solid",
                borderColor: selectedOption === "now" ? "#27ae60" : "#d5d8dc",
                borderRadius: 1.5,
                p: 2,
                mb: 1.5,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#27ae60",
                  backgroundColor: "#eafaf1",
                },
              }}
              onClick={() => setSelectedOption("now")}
            >
              <FormControlLabel
                value="now"
                control={<Radio sx={{ color: "#27ae60", "&.Mui-checked": { color: "#27ae60" } }} />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Send size={18} color="#27ae60" />
                    <Box>
                      <Typography fontWeight="600" fontSize="0.95rem">Send Now</Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        Process and send emails immediately
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>

            {/* Schedule Option */}
            <Box
              sx={{
                border: "2px solid",
                borderColor: selectedOption === "schedule" ? "#e67e22" : "#d5d8dc",
                borderRadius: 1.5,
                p: 2,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#e67e22",
                  backgroundColor: "#fef5e7",
                },
              }}
              onClick={() => setSelectedOption("schedule")}
            >
              <FormControlLabel
                value="schedule"
                control={<Radio sx={{ color: "#e67e22", "&.Mui-checked": { color: "#e67e22" } }} />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Clock size={18} color="#e67e22" />
                    <Box>
                      <Typography fontWeight="600" fontSize="0.95rem">Schedule for Later</Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        Choose a specific date and time
                      </Typography>
                    </Box>
                  </Box>
                }
              />

              {selectedOption === "schedule" && (
                <Box sx={{ mt: 2, ml: 3.5 }}>
                  <TextField
                    label="Select Date & Time"
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={handleDateTimeChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: dayjs().format('YYYY-MM-DDTHH:mm'),
                    }}
                  />
                </Box>
              )}
            </Box>
          </RadioGroup>
        </FormControl>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2, fontSize: "0.875rem" }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f8f9fa" }}>
        <Button
          onClick={handleClose}
        //   disabled={loading}
          sx={{
            textTransform: "none",
            color: "#7f8c8d",
            "&:hover": {
              backgroundColor: "#ecf0f1",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          sx={{
            textTransform: "none",
            background:
              selectedOption === "now"
                ? "linear-gradient(135deg, #27ae60 0%, #229954 100%)"
                : "linear-gradient(135deg, #e67e22 0%, #d35400 100%)",
            px: 3,
            "&:hover": {
              background:
                selectedOption === "now"
                  ? "linear-gradient(135deg, #229954 0%, #1e8449 100%)"
                  : "linear-gradient(135deg, #d35400 0%, #ba4a00 100%)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : selectedOption === "now" ? (
            "Send Now"
          ) : (
            "Schedule"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleOptions;